import React from 'react';
import { View, Text, Modal, TouchableOpacity } from 'react-native';
import { useTranslation } from '@oolio-group/localization';
import { KdsModifierStyle } from '@oolio-group/domain';
import ModifierStylePreviewModalStyles from './ModifierStylePreviewModal.styles';
import DocketItemStyles from '../../DocketItem/DocketItem.styles';
import Icon from '../../Icon/Icon';
import { useColors } from '../../../hooks/useColors';
import { matchModifierStyle } from '../../../utils/matchModifierStyle';

export interface ModifierStylePreviewModalProps {
  visible: boolean;
  styles: KdsModifierStyle[];
  onClose: () => void;
}

const PREVIEW_ORDER_TYPE = 'Dine In';
const PREVIEW_ORDER_NUMBER = '#1042';
const PREVIEW_TABLE_LABEL = 'Table 6';
const PREVIEW_TIME = '2m';
const PREVIEW_ITEM_NAME = 'Flat White';

// Realistic sample modifiers used to show what a non-matching option looks
// like. The first of these that matches none of the configured styles is
// shown in the default (unstyled) appearance.
const UNMATCHED_SAMPLES = ['No sugar', 'Extra hot', 'Regular', 'To stay'];

function titleCase(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return trimmed;
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
}

// This preview is a gallery of the configured styles: one demonstration
// modifier per active style, shown in *that* style's own colour/italic — so
// every configured style is visible even when a higher-priority style would
// shadow it on a real ticket. A single fallback row that matches no style is
// appended (verified via the shared matcher) so staff can see that
// non-matching options keep the default appearance.
function buildPreviewModifiers(
  modifierStyles: KdsModifierStyle[],
): { text: string; color?: string; italic?: boolean }[] {
  const styled = modifierStyles
    .filter(style => style.isActive !== false)
    .flatMap(style => {
      const keyword = style.conditions?.[0]?.keyword;
      const text = keyword ? titleCase(keyword) : style.name;
      if (!text) return [];
      return [
        { text, color: style.styles?.color, italic: style.styles?.italic },
      ];
    });

  const unmatched = UNMATCHED_SAMPLES.find(
    sample => !matchModifierStyle(sample, modifierStyles),
  );

  return unmatched ? [...styled, { text: unmatched }] : styled;
}

const ModifierStylePreviewModal: React.FC<ModifierStylePreviewModalProps> = ({
  visible,
  styles: modifierStyles,
  onClose,
}) => {
  const { translate } = useTranslation();
  const { colors } = useColors();
  const styles = ModifierStylePreviewModalStyles();
  const docketStyles = DocketItemStyles();

  const previewModifiers = buildPreviewModifiers(modifierStyles);

  return (
    <Modal
      animationType="fade"
      onDismiss={onClose}
      onRequestClose={onClose}
      presentationStyle="overFullScreen"
      transparent={true}
      visible={visible}
      supportedOrientations={['landscape', 'landscape-left', 'landscape-right']}
    >
      <View style={styles.background}>
        <View style={styles.card} testID="modifier-style-preview-modal">
          <View style={styles.header}>
            <Text style={styles.title}>
              {translate('kitchenDisplay.modifierStylingPreviewTitle')}
            </Text>
            <TouchableOpacity
              style={styles.closeButton}
              testID="btn-close-preview"
              onPress={onClose}
            >
              <Icon name="times" size={20} color={colors.text} />
            </TouchableOpacity>
          </View>

          <Text style={styles.caption}>
            {translate('kitchenDisplay.modifierStylingPreviewCaption')}
          </Text>

          <View style={styles.frame}>
            <View style={styles.docket}>
              <View style={[docketStyles.header, styles.docketHeader]}>
                <View style={docketStyles.headerContent}>
                  <View style={docketStyles.rowGroup}>
                    <View style={docketStyles.row}>
                      <Text style={docketStyles.text}>
                        {PREVIEW_ORDER_TYPE}
                      </Text>
                      <Text style={docketStyles.text}>
                        {PREVIEW_ORDER_NUMBER}
                      </Text>
                    </View>
                    <View style={docketStyles.row}>
                      <Text style={docketStyles.text}>
                        {PREVIEW_TABLE_LABEL}
                      </Text>
                      <Text style={docketStyles.text}>{PREVIEW_TIME}</Text>
                    </View>
                  </View>
                </View>
              </View>
              <View style={docketStyles.product}>
                <View style={[docketStyles.line, styles.itemRow]}>
                  <View style={[docketStyles.qty, styles.qty]}>
                    <Text style={docketStyles.qtyText}>1</Text>
                  </View>
                  <Text style={docketStyles.name}>{PREVIEW_ITEM_NAME}</Text>
                </View>
                {previewModifiers.map((modifier, index) => (
                  <View
                    key={`${modifier.text}-${index}`}
                    style={docketStyles.line}
                  >
                    <Text
                      style={[
                        docketStyles.modifierBase,
                        docketStyles.modifierSpacing,
                      ]}
                    >
                      {'↳ '}
                    </Text>
                    <Text
                      testID={`preview-ticket-modifier-${index}`}
                      style={[
                        docketStyles.modifierBase,
                        modifier.color && styles.modifierText,
                        modifier.color ? { color: modifier.color } : null,
                        modifier.italic ? styles.modifierItalic : null,
                      ]}
                    >
                      {modifier.text}
                    </Text>
                  </View>
                ))}
              </View>
            </View>
          </View>
        </View>
      </View>
    </Modal>
  );
};

export default ModifierStylePreviewModal;
